import { shallowRef } from "vue";
import { useApiClient } from "@/api/client";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import { processedDocument } from "chartjs-graph";
import {
	dataIdentityKey,
	docWithAllDisplaySettingsEnabled,
	metricKeys,
	reducibleIdentityKey,
} from "@/modules/explorer/composables/dataTableDisplaySettings";
import { toRawDeep } from "@/Utils/to-raw-deep";
import DocumentDO = App.Data.Explorer.DocumentDO;
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import DataTableDO = App.Data.Explorer.DataTableDO;

const supersetResponse = shallowRef<DataTableResponseDTO | null>(null);
const supersetTotals = shallowRef<Array<unknown> | null>(null);

const supersetKey = shallowRef<string | null>(null);

let supersetReducibleKey: string | null = null;
let supersetMetricKeys = new Set<string>();

// Not useAbortController: that hooks onUnmounted, which can't run at module scope.
let inFlightController: AbortController | null = null;
// in-flight prefetch's key, so a duplicate call for the same identity is a no-op.
let inFlightKey: string | null = null;

export function useDataTablePrefetch() {
	// Cache holds an exact match for this document (display-only change)
	const hasValidSuperset = (doc: DocumentDO): boolean =>
		supersetResponse.value !== null &&
		supersetKey.value === dataIdentityKey(doc);

	const canServeFromSuperset = (doc: DocumentDO): boolean => {
		if (supersetResponse.value === null || supersetReducibleKey === null) {
			return false;
		}
		if (reducibleIdentityKey(doc) !== supersetReducibleKey) {
			return false;
		}

		const currentMetrics = metricKeys(doc);

		if (!currentMetrics.every((metric) => supersetMetricKeys.has(metric))) {
			return false;
		}

		const removedMetric = currentMetrics.length < supersetMetricKeys.size;
		if (removedMetric && (supersetResponse.value.hasMoreRows ?? false)) {
			return false;
		}

		return true;
	};

	const isReducingOperation = (doc: DocumentDO): boolean =>
		canServeFromSuperset(doc) &&
		metricKeys(doc).length < supersetMetricKeys.size;

	const responseForSettings = (
		doc: DocumentDO
	): DataTableResponseDTO | null => {
		if (!canServeFromSuperset(doc) || supersetResponse.value === null) {
			return null;
		}

		const dataTableDO = toRawDeep<DataTableDO>(doc.data_tables[0]);

		return {
			...supersetResponse.value,
			dataTableDO,
			metrics: dataTableDO.metrics,
		};
	};

	const prefetch = async (
		doc: DocumentDO,
		routePrefix: string = "/"
	): Promise<void> => {
		const key = dataIdentityKey(doc);
		const reducibleKey = reducibleIdentityKey(doc);
		const fetchedMetricKeys = new Set(metricKeys(doc));

		if (supersetKey.value === key && supersetResponse.value !== null) {
			return;
		}
		if (inFlightKey === key && inFlightController) {
			return;
		}

		inFlightController?.abort();
		inFlightController = new AbortController();
		inFlightKey = key;
		const controller = inFlightController;

		const allEnabledDoc = processedDocument(
			docWithAllDisplaySettingsEnabled(doc)
		);

		const [response, totalsResponse] = await Promise.all([
			promptInvalidPermissions(
				useApiClient("explorer").fetchDataForDocumentDataTable(
					allEnabledDoc,
					routePrefix,
					controller.signal
				)
			),
			promptInvalidPermissions(
				useApiClient("explorer").fetchTotalsForDocumentDataTable(allEnabledDoc)
			),
		]);

		if (controller !== inFlightController) {
			return;
		}

		if (response.isOk()) {
			supersetResponse.value = response.value.data[0] ?? null;
			supersetTotals.value = totalsResponse.isOk()
				? totalsResponse.value
				: null;
			supersetKey.value = key;
			supersetReducibleKey = reducibleKey;
			supersetMetricKeys = fetchedMetricKeys;
		}

		inFlightController = null;
		inFlightKey = null;
	};

	const invalidate = (): void => {
		inFlightController?.abort();
		inFlightController = null;
		inFlightKey = null;
		supersetResponse.value = null;
		supersetTotals.value = null;
		supersetKey.value = null;
		supersetReducibleKey = null;
		supersetMetricKeys = new Set();
	};

	return {
		supersetResponse,
		supersetTotals,
		hasValidSuperset,
		canServeFromSuperset,
		isReducingOperation,
		responseForSettings,
		prefetch,
		invalidate,
	};
}
